import React, { useState, useEffect } from "react";
import axios from "axios";
import DinoLoader from "./DinoLoader";

const API_URL =
  window.location.hostname === "localhost"
    ? "http://127.0.0.1:8000/predict"
    : "https://uninfluential-krysten-hysterically.ngrok-free.dev/predict";


export default function UploadForm({ darkMode }) {
  const [file, setFile] = useState(null);
  const [formData, setFormData] = useState({
    blast_percent: "",
    wbc_count: "",
    hemoglobin: "",
    platelet_count: "",
    age: "",
    flt3_status: "N/A",
    npm1_status: "N/A",
    idh1_status: "N/A",
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showWarning, setShowWarning] = useState(false);

  useEffect(() => {
    const hasNA =
      Object.values(formData).includes("N/A") ||
      Object.values(formData).includes("");
    setShowWarning(hasNA);
  }, [formData]);

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) {
      alert("Please upload a blood smear image first!");
      return;
    }
    setLoading(true);
    const data = new FormData();
    data.append("image", file);
    Object.entries(formData).forEach(([key, val]) =>
      data.append(key, val === "N/A" ? "" : val)
    );

    try {
      const res = await axios.post(API_URL, data);
      console.log("AI backend response:", res.data);
      setResult(res.data);
    } catch (err) {
      alert("Error connecting to AI backend.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <form onSubmit={handleSubmit} style={{ display: "grid", gap: "1.5rem" }}>
        <section>
          <h3>🩸 Blood Smear Image</h3>
          <input
            type="file"
            accept=".tiff,.tif,.jpg,.png"
            onChange={(e) => setFile(e.target.files[0])}
          />
        </section>

        <section>
          <h3>⚕️ Clinical Data</h3>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(2, 1fr)",
              gap: "1rem",
            }}
          >
            {[
              ["blast_percent", "Blast %"],
              ["wbc_count", "WBC Count (×10³/µL)"],
              ["hemoglobin", "Hemoglobin (g/dL)"],
              ["platelet_count", "Platelet Count (×10⁶/µL)"],
              ["age", "Age (years)"],
            ].map(([name, label]) => (
              <div key={name}>
                <label>{label}</label>
                <input
                  name={name}
                  type="number"
                  value={formData[name]}
                  onChange={handleChange}
                />
              </div>
            ))}
          </div>
        </section>

        <section>
          <h3>🧬 Genetic Markers</h3>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: "1rem",
            }}
          >
            {["flt3_status", "npm1_status", "idh1_status"].map((gene) => (
              <div key={gene}>
                <label>{gene.replace("_status", "").toUpperCase()} Mutation</label>
                <select
                  name={gene}
                  value={formData[gene]}
                  onChange={handleChange}
                >
                  <option value="N/A">N/A</option>
                  <option value="Negative">Negative</option>
                  <option value="Positive">Positive</option>
                </select>
              </div>
            ))}
          </div>
        </section>

        {showWarning && (
          <div
            style={{
              background: darkMode ? "#5c2e00" : "#fff3cd",
              color: darkMode ? "#ffcd69" : "#856404",
              padding: "0.8rem 1rem",
              borderRadius: "8px",
            }}
          >
            ⚠️ Some values are N/A or blank. Prediction accuracy may be reduced.
          </div>
        )}

        <button type="submit" disabled={loading}>
          {loading ? "Analyzing..." : "Run Chet Jeppiti AI Analysis"}
        </button>

        {loading && (
          <div className={`dino-loader ${loading ? "active" : ""}`}>
            <DinoLoader darkMode={darkMode} />
          </div>
        )}
      </form>

      {result && (
        <div
          className="result-card"
          style={{
            background: darkMode
              ? result.category === "AML Probability Risk"
                ? "#3b0000"
                : result.category === "Risk for Other Leukemia"
                ? "#3b2a00"
                : "#003b00"
              : result.category === "AML Probability Risk"
              ? "#ffebee"
              : result.category === "Risk for Other Leukemia"
              ? "#fff3e0"
              : "#e8f5e9",
            border: `2px solid ${
              result.category === "AML Probability Risk"
                ? "#e53935"
                : result.category === "Risk for Other Leukemia"
                ? "#f9a825"
                : "#00c853"
            }`,
            color: darkMode ? "#fff" : "#222",
            padding: "1.5rem",
            borderRadius: "12px",
            transition: "background 0.4s ease, color 0.3s ease",
            marginTop: "2rem",
            boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
          }}
        >
          <h3>🧠 AI Analysis Results</h3>
          <p>
            <b>Predicted Category:</b> {result.category}
          </p>
          <p>
            <b>Integrated AML Risk (%):</b> {result.integrated_risk}
          </p>

          {/* Progress bar */}
          <div
            style={{
              background: darkMode ? "#555" : "#333",
              borderRadius: "8px",
              overflow: "hidden",
              height: "15px",
              margin: "10px 0",
            }}
          >
            <div
              style={{
                width: `${result.integrated_risk}%`,
                background:
                  result.integrated_risk > 70
                    ? "#e53935"
                    : result.integrated_risk > 30
                    ? "#f9a825"
                    : "#00c853",
                height: "100%",
                transition: "width 0.5s ease",
              }}
            ></div>
          </div>

          <p>
            <b>CNN Blast Probability:</b>{" "}
            {result.cnn_blast_prob ? result.cnn_blast_prob.toFixed(2) : "N/A"}
          </p>

          {/* Morphology Features */}
          {Array.isArray(result?.cnn_features) && result.cnn_features.length > 0 && (
            <>
              <h4 style={{ marginTop: "1.5rem" }}>🧬 Morphology Feature Strengths</h4>
              {result.cnn_features.map((val, idx) => {
                const label = Array.isArray(result.feature_labels)
                  ? result.feature_labels[idx]
                  : `Feature ${idx + 1}`;
                const featureDescriptions = {
                  "Nuclear irregularity": "Measures nucleus shape distortion.",
                  "Blast-like morphology": "Shows resemblance to immature blast cells.",
                  "Granule density": "Cytoplasmic granularity level.",
                  "Cytoplasmic-to-nuclear ratio": "Relative cytoplasm vs nucleus size.",
                  "Cell clustering uniformity": "Distribution uniformity of cells.",
                };
                const desc = featureDescriptions[label] || "";
                return (
                  <div key={idx} style={{ marginBottom: "0.8rem" }}>
                    <div
                      title={desc}
                      style={{
                        fontSize: "0.9rem",
                        marginBottom: "0.3rem",
                        cursor: desc ? "help" : "default",
                      }}
                    >
                      <b>{label}</b> — {val.toFixed(2)}
                    </div>
                    <div
                      title={desc}
                      style={{
                        background: darkMode ? "#444" : "#ddd",
                        borderRadius: "6px",
                        height: "8px",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          width: `${val * 100}%`,
                          background:
                            val > 0.7
                              ? "#e53935"
                              : val > 0.4
                              ? "#f9a825"
                              : "#00c853",
                          height: "100%",
                          transition: "width 0.5s ease",
                        }}
                      ></div>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>
      )}
    </div>
  );
}
